import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { supabase } from "@shared/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Combobox } from "@/components/ui/combobox";

import type { Judge } from "@/lib/judges";

interface JudgeCredentialsDialogProps {
	judge: Judge;
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onUpdate: () => void;
}

const formSchema = z.object({
	model: z.string().min(1, "Model is required"),
	max_tokens: z.number().min(1).max(128000),
	temperature: z.number().min(0).max(2),
});

type FormData = z.infer<typeof formSchema>;

const OPENROUTER_MODELS = [
	// OpenAI Models - Fast
	{ value: "openai/gpt-4o", label: "GPT-4o" },
	{ value: "openai/gpt-4o-mini", label: "GPT-4o Mini" },
	{ value: "openai/gpt-5.2", label: "GPT-5.2" },
	{ value: "openai/gpt-5.2-chat", label: "GPT-5.2 Chat" },
	// OpenAI Models - Reasoning
	{ value: "openai/o1", label: "o1" },
	{ value: "openai/o3-mini", label: "o3 Mini" },
	{ value: "openai/o3", label: "o3" },
	// Anthropic Models - Fast
	{ value: "anthropic/claude-3.5-sonnet", label: "Claude 3.5 Sonnet" },
	{ value: "anthropic/claude-3.5-haiku", label: "Claude 3.5 Haiku" },
	{ value: "anthropic/claude-sonnet-4.5", label: "Claude Sonnet 4.5" },
	{ value: "anthropic/claude-opus-4.5", label: "Claude Opus 4.5" },
	{ value: "anthropic/claude-haiku-4.5", label: "Claude Haiku 4.5" },
	// Anthropic Models - Thinking
	{ value: "anthropic/claude-3.7-sonnet", label: "Claude 3.7 Sonnet (Thinking)" },
	// Google Models - Fast
	{ value: "google/gemini-2.5-flash", label: "Gemini 2.5 Flash" },
	{ value: "google/gemini-2.5-pro", label: "Gemini 2.5 Pro" },
	{ value: "google/gemini-3-flash-preview", label: "Gemini 3 Flash Preview" },
	// Google Models - Thinking
	{ value: "google/gemini-3-pro-preview", label: "Gemini 3 Pro Preview" },
];

export const JudgeCredentialsDialog = ({
	judge,
	open,
	onOpenChange,
	onUpdate,
}: JudgeCredentialsDialogProps) => {
	const { toast } = useToast();
	const [isLoading, setIsLoading] = useState(false);

	const form = useForm<FormData>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			model: judge.model,
			max_tokens: judge.max_tokens,
			temperature: judge.temperature,
		},
	});

	const onSubmit = async (data: FormData) => {
		setIsLoading(true);
		try {
			const { data: authData } = await supabase.auth.getUser();
			if (!authData.user?.id) {
				toast({
					title: "Error",
					description: "You must be signed in to update judge credentials.",
					variant: "destructive",
				});
				return;
			}

			// Update judge configuration
			const { error: judgeError } = await supabase
				.from("judges")
				.update({
					model: data.model,
					max_tokens: data.max_tokens,
					temperature: data.temperature,
				})
				.eq("id", judge.id);

			if (judgeError) throw judgeError;

			toast({
				title: "Success",
				description: "Judge credentials updated successfully",
			});

			onUpdate();
			onOpenChange(false);
			form.reset();
		} catch (error) {
			console.error("Error saving judge credentials:", error);
			toast({
				title: "Error",
				description: "Failed to save judge credentials",
				variant: "destructive",
			});
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
				<DialogContent className="sm:max-w-[500px]">
					<DialogHeader>
						<DialogTitle>Configure Judge Settings</DialogTitle>
						<DialogDescription>
							Set up the AI model settings for this judge.
						</DialogDescription>
					</DialogHeader>
				<Form {...form}>
					<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
						<FormField
							control={form.control}
							name="model"
							render={({ field }) => (
								<FormItem>
									<FormLabel>Model</FormLabel>
									<FormControl>
										<Combobox
											options={OPENROUTER_MODELS}
											value={field.value}
											onValueChange={field.onChange}
											placeholder="Select or type a model..."
											searchPlaceholder="Search or type model name..."
											emptyMessage="No matching model found."
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="max_tokens"
							render={({ field }) => (
								<FormItem>
									<FormLabel>Max Tokens</FormLabel>
									<FormControl>
										<Input
											type="number"
											{...field}
											onChange={(e) => field.onChange(parseInt(e.target.value))}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="temperature"
							render={({ field }) => (
								<FormItem>
									<FormLabel>Temperature</FormLabel>
									<FormControl>
										<Input
											type="number"
											step="0.1"
											{...field}
											onChange={(e) =>
												field.onChange(parseFloat(e.target.value))
											}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<div className="flex justify-end gap-2">
							<Button
								type="button"
								variant="outline"
								onClick={() => onOpenChange(false)}
							>
								Cancel
							</Button>
							<Button type="submit" disabled={isLoading}>
								{isLoading ? "Saving..." : "Save Configuration"}
							</Button>
						</div>
					</form>
				</Form>
			</DialogContent>
		</Dialog>
	);
};
